import type { AxiosInstance } from 'axios';

import { throwApiError } from 'src/api/utils';
import type { TTableSettingsModel } from 'src/services/user-settings-service/types';
import { isObjectWithKeys } from 'src/shared/utils/is-object-with-keys';

export class WellsTableTemplatesApi {
  private readonly agent: AxiosInstance;

  constructor(agent: AxiosInstance) {
    this.agent = agent;
  }

  async getTemplates(): Promise<TWellTableTemplates> {
    try {
      const { data } = await this.agent.get<TWellTableTemplates>('userSettings/wellTableTemplates');

      if (!data || !isObjectWithKeys(data)) {
        await this.createTemplates({ private: [], system: [] }, new AbortSignal());

        return {
          private: [],
          system: [],
        };
      }

      return {
        private: data.private.map((t) => ({ ...t, type: 'private' })),
        system: data.system.map((t) => ({ ...t, type: 'system' })),
      };
    } catch (error) {
      throwApiError(error);
    }
  }

  async createTemplates(
    templates: {
      private: TWellTableTemplate[];
      system: TWellTableTemplate[];
    },
    signal: AbortSignal,
  ): Promise<TWellTableTemplates> {
    try {
      const { data } = await this.agent.post<TWellTableTemplates>('userSettings/wellTableTemplates', templates, {
        signal,
      });

      return data;
    } catch (error) {
      throwApiError(error);
    }
  }

  async updateTemplates(
    templates: {
      private: TWellTableTemplate[];
      system: TWellTableTemplate[];
    },
    signal: AbortSignal,
  ): Promise<TWellTableTemplates> {
    try {
      const { data } = await this.agent.patch<TWellTableTemplates>('userSettings/wellTableTemplates', templates, {
        signal,
      });

      return data;
    } catch (error) {
      throwApiError(error);
    }
  }
}

export type TWellTableTemplates = {
  private: TWellTableTemplate[];
  system: TWellTableTemplate[];
};

export type TWellTableTemplateData = Omit<TTableSettingsModel, 'ownerUserId'>;

export type TWellTableTemplate = {
  id: string;
  type: 'system' | 'private';
  name: string;
  description?: string;
  data: TWellTableTemplateData;
};
